import React, { useEffect, useState, useMemo } from 'react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { Helmet } from 'react-helmet';
import api from '../../services/api';
import '../../assets/css/video.css';

const VIDEOS_PER_PAGE = 10;

const Video = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Function to extract YouTube video ID from URL
const getYouTubeId = (url) => {
  if (!url) return null;

  try {
    const urlObj = new URL(url);

    // youtube.com/watch?v=VIDEO_ID
    if (urlObj.hostname.includes('youtube.com')) {
      const videoId = urlObj.searchParams.get('v');

      if (videoId) {
        return videoId;
      }

      // youtube.com/embed/VIDEO_ID
      // youtube.com/shorts/VIDEO_ID
      // youtube.com/live/VIDEO_ID
      const pathParts = urlObj.pathname.split('/').filter(Boolean);

      if (
        ['embed', 'shorts', 'live', 'v'].includes(pathParts[0]) &&
        pathParts[1]
      ) {
        return pathParts[1];
      }
    }

    // youtu.be/VIDEO_ID
    if (urlObj.hostname === 'youtu.be') {
      return urlObj.pathname.split('/').filter(Boolean)[0] || null;
    }

    return null;
  } catch (error) {
    console.error('Invalid YouTube URL:', url);
    return null;
  }
};

  // Function to get YouTube thumbnail URL
const getYouTubeThumbnail = (url, quality = 'hqdefault') => {
  const videoId = getYouTubeId(url);

  if (!videoId) {
    console.warn('Could not extract YouTube ID:', url);
    return null;
  }

  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};
  // Strip HTML tags/entities from rich-text descriptions so raw markup
  // (like literal <p> tags) never shows up as visible text in the cards.
  const stripHtml = (html) => {
    if (!html) return '';
    const text = html
      .replace(/<\/(p|div|li|br|h[1-6])>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&#39;|&apos;/gi, "'")
      .replace(/&quot;/gi, '"')
      .replace(/\s+/g, ' ')
      .trim();
    return text;
  };

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await api.get('/videos');

        if (!response.data || !response.data.success || !Array.isArray(response.data.data)) {
          throw new Error('Invalid data format received from API');
        }

        // Enhance videos with YouTube thumbnails
        const enhancedVideos = response.data.data.map(video => ({
          ...video,
          youtube_thumbnail: getYouTubeThumbnail(video.video_url)
        }));

        setVideos(enhancedVideos);

        const activeVideos = enhancedVideos.filter(video => Boolean(video.is_active));

        if (activeVideos.length > 0) {
          setFeaturedVideo(activeVideos[0]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError(err.message || 'Failed to load videos');
        setLoading(false);
      }
    };

    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeVideos = useMemo(
    () => videos.filter(v => v.is_active && v.id !== featuredVideo?.id),
    [videos, featuredVideo]
  );

  const totalPages = Math.max(1, Math.ceil(activeVideos.length / VIDEOS_PER_PAGE));

  const paginatedVideos = useMemo(() => {
    const start = (currentPage - 1) * VIDEOS_PER_PAGE;
    return activeVideos.slice(start, start + VIDEOS_PER_PAGE);
  }, [activeVideos, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    const grid = document.getElementById('video-grid-top');
    if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const pageNumbers = useMemo(() => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }, [totalPages]);

  const HeroSection = () => (
    <section className="vp-hero">
      <div className="container text-center">
        <span className="vp-hero-eyebrow">Video Library</span>
        <h1 className="vp-hero-title">Our Videos</h1>
        <p className="vp-hero-subtitle">
          Explore helpful videos about our plans, features, pricing, and services.
          Get a closer look at how everything works and find the right plan for your needs.
        </p>
      </div>
    </section>
  );

  if (loading) {
    return (
      <div className="video-page vp-page">
        <Helmet><title>Loading Videos...</title></Helmet>
        <Navbar />
        <VideoPageStyles />
        <HeroSection />
        <div className="container text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3">Loading video content...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-page vp-page">
        <Helmet><title>Error Loading Videos</title></Helmet>
        <Navbar />
        <VideoPageStyles />
        <HeroSection />
        <div className="container text-center py-5">
          <div className="alert alert-danger">
            <h5>Error Loading Videos</h5>
            <p>{error}</p>
            <button
              className="btn btn-primary mt-2"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="video-page vp-page">
      <Helmet>
        <title>Videos - Startup Business Partners</title>
      </Helmet>

      <Navbar />
      <VideoPageStyles />
      <HeroSection />

      <div className="vp-wrapper">
        <div className="container">

          {/* ====================== FEATURED VIDEO ====================== */}
          {featuredVideo ? (
            <div className="vp-featured">
              <div className="vp-featured-media">
                {featuredVideo.youtube_thumbnail ? (
                  <img
                    src={featuredVideo.youtube_thumbnail}
                    alt={featuredVideo.title}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = featuredVideo.thumbnail_url || '/images/video-placeholder.jpg';
                    }}
                  />
                ) : featuredVideo.thumbnail_url ? (
                  <img src={featuredVideo.thumbnail_url} alt={featuredVideo.title} />
                ) : (
                  <div className="vp-placeholder">No Thumbnail Available</div>
                )}
                <a
                  href={featuredVideo.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vp-play-btn vp-play-btn-lg"
                  aria-label="Play featured video"
                >
                  <svg viewBox="0 0 415.346 415.346" xmlns="http://www.w3.org/2000/svg">
                    <path d="M41.712,415.346c-11.763,0-21.3-9.537-21.3-21.3V21.299C20.412,9.536,29.949,0,41.712,0l346.122,191.697c0,0,15.975,15.975,0,31.951C371.859,239.622,41.712,415.346,41.712,415.346z" />
                  </svg>
                </a>
              </div>

              <div className="vp-featured-info">
                <span className="vp-featured-tag">Featured Video</span>
                <h2>{featuredVideo.title}</h2>
                <p>{stripHtml(featuredVideo.description)}</p>
                <a
                  href={featuredVideo.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vp-btn vp-btn-primary"
                >
                  Watch Full Video
                </a>
              </div>
            </div>
          ) : (
            <div className="alert alert-warning text-center">No featured video available</div>
          )}

          {/* ====================== VIDEO GRID ====================== */}
          <div id="video-grid-top" className="vp-more-header">
            <h3>More Videos</h3>
            {activeVideos.length > 0 && (
              <span className="vp-count">{activeVideos.length} videos</span>
            )}
          </div>

          {paginatedVideos.length > 0 ? (
            <>
              <div className="vp-grid">
                {paginatedVideos.map(video => (
                  <div key={video.id} className="vp-card">
                    <a
                      href={video.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="vp-card-media"
                    >
                      {video.youtube_thumbnail ? (
                        <img
                          src={video.youtube_thumbnail}
                          alt={video.title}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = video.thumbnail_url || '/images/video-placeholder.jpg';
                          }}
                        />
                      ) : video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt={video.title} />
                      ) : (
                        <div className="vp-placeholder">No Thumbnail</div>
                      )}
                      <span className="vp-play-btn vp-play-btn-sm">
                        <i className="las la-play"></i>
                      </span>
                    </a>

                    <div className="vp-card-body">
                      <h5>{video.title}</h5>
                      <p className="vp-card-desc">{stripHtml(video.description)}</p>
                      <a
                        href={video.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="vp-btn vp-btn-outline"
                      >
                        Watch Now
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* ====================== PAGINATION ====================== */}
              {totalPages > 1 && (
                <nav className="vp-pagination" aria-label="Video pagination">
                  <button
                    className="vp-page-btn vp-page-nav"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ‹ Prev
                  </button>

                  {pageNumbers.map((num) => (
                    <button
                      key={num}
                      className={`vp-page-btn ${currentPage === num ? 'vp-page-active' : ''}`}
                      onClick={() => goToPage(num)}
                    >
                      {num}
                    </button>
                  ))}

                  <button
                    className="vp-page-btn vp-page-nav"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next ›
                  </button>
                </nav>
              )}
            </>
          ) : (
            <div className="alert alert-info text-center">No videos available</div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

const VideoPageStyles = () => (
  <style>{`
    .vp-page {
      --vp-ink: #16202b;
      --vp-ink-soft: #5b6b7c;
      --vp-line: #e3e8ee;
      --vp-surface: #ffffff;
      --vp-canvas: #f4f6f9;
      --vp-accent: #2b5cb8;
      --vp-accent-soft: #eaf1ff;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      color: var(--vp-ink);
      background: var(--vp-canvas);
    }

    .vp-hero {
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      padding: 84px 0 76px;
      color: #fff;
    }
    .vp-hero-eyebrow {
      display: inline-block;
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #9db8ec;
      margin-bottom: 14px;
    }
    .vp-hero-title {
      font-size: clamp(2rem, 4vw, 2.7rem);
      font-weight: 700;
      margin-bottom: 16px;
      color: #fff;
    }
    .vp-hero-subtitle {
      font-size: 1.1rem;
      opacity: 0.88;
      max-width: 780px;
      margin: 0 auto;
      line-height: 1.7;
    }

    .vp-wrapper { padding: 56px 0 72px; }

    /* ---------- FEATURED ---------- */
    .vp-featured {
      display: grid;
      grid-template-columns: 1.1fr 1fr;
      gap: 40px;
      align-items: center;
      background: var(--vp-surface);
      border: 1px solid var(--vp-line);
      border-radius: 20px;
      padding: 28px;
      margin-bottom: 56px;
      box-shadow: 0 1px 3px rgba(16,24,40,0.05);
    }
    @media (max-width: 900px) {
      .vp-featured { grid-template-columns: 1fr; }
    }

    .vp-featured-media {
      position: relative;
      border-radius: 14px;
      overflow: hidden;
      aspect-ratio: 16 / 9;
      background: #0f172a;
    }
    .vp-featured-media img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .vp-featured-tag {
      display: inline-block;
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--vp-accent);
      background: var(--vp-accent-soft);
      padding: 4px 12px;
      border-radius: 999px;
      margin-bottom: 12px;
    }
    .vp-featured-info h2 {
      font-size: 1.6rem;
      font-weight: 700;
      margin-bottom: 12px;
      color: var(--vp-ink);
    }
    .vp-featured-info p {
      color: var(--vp-ink-soft);
      font-size: 1rem;
      line-height: 1.7;
      margin-bottom: 20px;
    }

    /* ---------- MORE VIDEOS HEADER ---------- */
    .vp-more-header {
      display: flex;
      align-items: baseline;
      justify-content: center;
      gap: 12px;
      margin-bottom: 28px;
      scroll-margin-top: 90px;
    }
    .vp-more-header h3 {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0;
      text-align: center;
    }
    .vp-count {
      color: var(--vp-ink-soft);
      font-size: 0.9rem;
      font-weight: 600;
    }

    /* ---------- GRID ---------- */
    .vp-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 26px;
      margin-bottom: 40px;
    }

    .vp-card {
      background: var(--vp-surface);
      border: 1px solid var(--vp-line);
      border-radius: 16px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    }
    .vp-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(16,24,40,0.08);
    }

    .vp-card-media {
      position: relative;
      display: block;
      aspect-ratio: 16 / 9;
      background: #0f172a;
    }
    .vp-card-media img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .vp-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #cbd5e1;
      font-size: 0.85rem;
      background: #0f172a;
    }

    .vp-play-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255,255,255,0.92);
      color: var(--vp-accent);
      border-radius: 50%;
      transition: transform 0.15s ease, background 0.15s ease;
    }
    .vp-play-btn-lg {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 72px; height: 72px;
      padding: 0;
    }
    .vp-play-btn-lg svg { width: 26px; height: 26px; fill: var(--vp-accent); margin-left: 4px; }
    .vp-play-btn-lg:hover { transform: translate(-50%, -50%) scale(1.08); }

    .vp-play-btn-sm {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 46px; height: 46px;
      font-size: 1.05rem;
    }
    .vp-card-media:hover .vp-play-btn-sm { background: #fff; transform: translate(-50%, -50%) scale(1.08); }

    .vp-card-body {
      padding: 18px 20px 20px;
      display: flex;
      flex-direction: column;
      flex-grow: 1;
    }
    .vp-card-body h5 {
      font-size: 1.02rem;
      font-weight: 700;
      margin-bottom: 8px;
      color: var(--vp-ink);
    }
    .vp-card-desc {
      color: var(--vp-ink-soft);
      font-size: 0.9rem;
      line-height: 1.55;
      margin-bottom: 16px;
      flex-grow: 1;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .vp-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      padding: 0 18px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.88rem;
      text-decoration: none;
      align-self: flex-start;
      transition: opacity 0.15s ease, background 0.15s ease;
    }
    .vp-btn-primary { background: var(--vp-accent); color: #fff; }
    .vp-btn-primary:hover { opacity: 0.9; color: #fff; }
    .vp-btn-outline {
      background: transparent;
      color: var(--vp-accent);
      border: 1.5px solid var(--vp-accent);
    }
    .vp-btn-outline:hover { background: var(--vp-accent-soft); color: var(--vp-accent); }

    /* ---------- PAGINATION ---------- */
    .vp-pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 8px;
    }
    .vp-page-btn {
      min-width: 40px;
      height: 40px;
      padding: 0 12px;
      border-radius: 8px;
      border: 1.5px solid var(--vp-line);
      background: var(--vp-surface);
      color: var(--vp-ink-soft);
      font-weight: 700;
      font-size: 0.9rem;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .vp-page-btn:hover:not(:disabled) {
      border-color: var(--vp-accent);
      color: var(--vp-accent);
    }
    .vp-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .vp-page-active {
      background: var(--vp-accent);
      border-color: var(--vp-accent);
      color: #fff;
    }
    .vp-page-nav { padding: 0 16px; }
  `}</style>
);

export default Video;